/**
 * JOY PRODUCT JAVASCRIPT v5.0 — Amazon-style variant picker
 *
 * RULES:
 * 1. Page loads with NO variant selected — all images visible as gallery,
 *    price shows range, Add to Cart says "SELECT OPTIONS".
 * 2. Clicking an option SELECTS it. Clicking it again DESELECTS it.
 * 3. "Clear selections" button resets everything to the unselected state.
 * 4. Greyed-out (unavailable) options are STILL CLICKABLE.
 *    Selecting one auto-corrects other options to the first available combo.
 * 5. Once all options are selected, a variant is resolved: price, image, SKU update.
 */

(function () {
  "use strict";

  // ============================================
  // UTILITIES
  // ============================================

  function getWishlist() {
    try { return JSON.parse(localStorage.getItem("joy-wishlist")) || []; }
    catch (e) { return []; }
  }

  function saveWishlist(list) {
    try { localStorage.setItem("joy-wishlist", JSON.stringify(list)); }
    catch (e) {}
  }

  function updateCartCount() {
    fetch("/cart.js")
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll(".cart-count, [data-cart-count], .cart-count-bubble span")
          .forEach(function (el) { el.textContent = cart.item_count; });
      })
      .catch(function () {});
  }

  function formatMoney(cents) {
    return "$" + (cents / 100).toFixed(2);
  }

  // ============================================
  // GSAP HELPERS (safe — no opacity on page elements)
  // ============================================

  var gsapReady = typeof gsap !== "undefined";

  function animateImageIn(img) {
    img.style.display = "block";
    if (gsapReady) {
      gsap.fromTo(img,
        { opacity: 0, scale: 1.04 },
        { opacity: 1, scale: 1, duration: 0.45, ease: "power2.out" }
      );
    }
  }

  function animateImageOut(img) {
    if (!gsapReady) { img.style.display = "none"; return; }
    gsap.to(img, {
      opacity: 0, duration: 0.2, ease: "power2.in",
      onComplete: function () { img.style.display = "none"; }
    });
  }

  function animateThumbnailPop(thumb) {
    if (gsapReady) {
      gsap.fromTo(thumb, { scale: 0.85 }, { scale: 1, duration: 0.3, ease: "back.out(2)" });
    }
  }

  // ============================================
  // GET / SET SELECTED OPTIONS (null = not selected)
  // ============================================

  function getSelectedOptions(section) {
    var result = [];
    section.querySelectorAll(".joy-variant-group").forEach(function (group, i) {
      var swatch = group.querySelector(".joy-color-swatch--selected");
      var size = group.querySelector(".joy-size-btn--selected");
      var dropdown = group.querySelector(".joy-select");
      if (swatch) result[i] = swatch.dataset.value;
      else if (size) result[i] = size.dataset.value;
      else if (dropdown && dropdown.value !== "") result[i] = dropdown.value;
      else result[i] = null;
    });
    return result;
  }

  function hasAnySelection(selectedOptions) {
    return selectedOptions.some(function (v) { return v !== null; });
  }

  function allSelected(selectedOptions) {
    return selectedOptions.every(function (v) { return v !== null; });
  }

  // ============================================
  // MEDIA HELPERS
  // ============================================

  function getMediaIdsForColor(product, colorOptionIndex, colorValue) {
    var mediaIds = [];
    product.variants.forEach(function (v) {
      if (v.options[colorOptionIndex] === colorValue && v.featured_media) {
        var mid = String(v.featured_media.id);
        if (mediaIds.indexOf(mid) === -1) mediaIds.push(mid);
      }
    });
    return mediaIds;
  }

  function showAllMedia(section) {
    var images = section.querySelectorAll(".joy-product__image");
    var thumbnails = section.querySelectorAll(".joy-product__thumbnail");

    images.forEach(function (img, i) {
      if (i === 0) {
        img.style.display = "block";
        img.style.opacity = "1";
        img.classList.add("is-active");
      } else {
        img.style.display = "none";
        img.style.opacity = "1";
        img.classList.remove("is-active");
      }
    });

    thumbnails.forEach(function (t, i) {
      t.style.display = "";
      t.classList.toggle("joy-product__thumbnail--active", i === 0);
    });
  }

  function showImagesForColor(section, product, colorOptionIndex, colorValue) {
    var mediaIds = getMediaIdsForColor(product, colorOptionIndex, colorValue);
    var images = section.querySelectorAll(".joy-product__image");
    var thumbnails = section.querySelectorAll(".joy-product__thumbnail");

    if (mediaIds.length === 0) {
      showAllMedia(section);
      return;
    }

    var firstShown = false;
    images.forEach(function (img) {
      var match = mediaIds.indexOf(img.dataset.mediaId) !== -1;
      if (match && !firstShown) {
        animateImageIn(img);
        img.classList.add("is-active");
        firstShown = true;
      } else if (match) {
        img.style.display = "none";
        img.style.opacity = "1";
        img.classList.remove("is-active");
      } else {
        animateImageOut(img);
        img.classList.remove("is-active");
      }
    });

    var firstThumbShown = false;
    var visibleThumbs = [];
    thumbnails.forEach(function (t) {
      var match = mediaIds.indexOf(t.dataset.mediaId) !== -1;
      t.style.display = match ? "" : "none";
      if (match) {
        t.classList.toggle("joy-product__thumbnail--active", !firstThumbShown);
        visibleThumbs.push(t);
        firstThumbShown = true;
      } else {
        t.classList.remove("joy-product__thumbnail--active");
      }
    });

    if (gsapReady && visibleThumbs.length) {
      gsap.fromTo(visibleThumbs,
        { opacity: 0, y: 8 },
        { opacity: 1, y: 0, duration: 0.35, stagger: 0.05, ease: "power2.out" }
      );
    }
  }

  function showImageForVariant(section, variant) {
    if (!variant || !variant.featured_media) return;
    var targetId = String(variant.featured_media.id);
    var images = section.querySelectorAll(".joy-product__image");
    var thumbnails = section.querySelectorAll(".joy-product__thumbnail");

    images.forEach(function (img) {
      if (img.dataset.mediaId === targetId) {
        animateImageIn(img);
        img.classList.add("is-active");
      } else if (img.style.display !== "none") {
        animateImageOut(img);
        img.classList.remove("is-active");
      }
    });

    thumbnails.forEach(function (t) {
      if (t.style.display !== "none") {
        t.classList.toggle("joy-product__thumbnail--active", t.dataset.mediaId === targetId);
      }
    });
  }

  // ============================================
  // AVAILABILITY DISPLAY (visual only, nothing disabled)
  // ============================================

  function updateOptionAvailability(section, product) {
    var selectedOptions = getSelectedOptions(section);
    var groups = section.querySelectorAll(".joy-variant-group");

    groups.forEach(function (group, groupIdx) {
      var swatches = group.querySelectorAll(".joy-color-swatch");
      var sizeBtns = group.querySelectorAll(".joy-size-btn");
      var dropdown = group.querySelector(".joy-select");
      var buttons = swatches.length > 0 ? swatches : sizeBtns;

      buttons.forEach(function (btn) {
        var testValue = btn.dataset.value;

        var hasAvailable = product.variants.some(function (v) {
          if (v.options[groupIdx] !== testValue) return false;
          return selectedOptions.every(function (opt, i) {
            if (i === groupIdx) return true;
            if (opt === null) return true;
            return v.options[i] === opt;
          }) && v.available;
        });

        var hasAnyVariant = product.variants.some(function (v) {
          if (v.options[groupIdx] !== testValue) return false;
          return selectedOptions.every(function (opt, i) {
            if (i === groupIdx) return true;
            if (opt === null) return true;
            return v.options[i] === opt;
          });
        });

        btn.classList.toggle("joy-option--unavailable", !hasAvailable && hasAnyVariant);
        btn.classList.toggle("joy-option--nonexistent", !hasAnyVariant);
        btn.disabled = false;
      });

      if (dropdown) {
        dropdown.querySelectorAll("option").forEach(function (opt) {
          if (opt.value === "") return;
          var testValue = opt.value;
          var hasAvailable = product.variants.some(function (v) {
            if (v.options[groupIdx] !== testValue) return false;
            return selectedOptions.every(function (sel, i) {
              if (i === groupIdx) return true;
              if (sel === null) return true;
              return v.options[i] === sel;
            }) && v.available;
          });
          var hasAnyVariant = product.variants.some(function (v) {
            if (v.options[groupIdx] !== testValue) return false;
            return selectedOptions.every(function (sel, i) {
              if (i === groupIdx) return true;
              if (sel === null) return true;
              return v.options[i] === sel;
            });
          });
          opt.disabled = false;
          var label = testValue;
          if (!hasAnyVariant) label = testValue + " — Unavailable";
          else if (!hasAvailable) label = testValue + " — Sold out";
          opt.textContent = label;
        });
      }
    });
  }

  // ============================================
  // BI-DIRECTIONAL AUTO-SELECT
  // When you pick an option that creates an invalid combo,
  // auto-correct the OTHER options to the first available match.
  // ============================================

  function autoCorrectOtherOptions(section, product, changedGroupIdx) {
    var selectedOptions = getSelectedOptions(section);

    var hasValidVariant = product.variants.some(function (v) {
      return selectedOptions.every(function (opt, i) {
        if (opt === null) return true;
        return v.options[i] === opt;
      });
    });

    if (hasValidVariant) return;

    var groups = section.querySelectorAll(".joy-variant-group");

    groups.forEach(function (group, gIdx) {
      if (gIdx === changedGroupIdx) return;
      if (selectedOptions[gIdx] === null) return;

      var testOptions = selectedOptions.slice();
      testOptions[gIdx] = null;

      var bestValue = null;
      product.variants.some(function (v) {
        var matches = testOptions.every(function (opt, i) {
          if (opt === null) return true;
          return v.options[i] === opt;
        });
        if (matches && v.available) {
          bestValue = v.options[gIdx];
          return true;
        }
        return false;
      });

      if (!bestValue) {
        product.variants.some(function (v) {
          var matches = testOptions.every(function (opt, i) {
            if (opt === null) return true;
            return v.options[i] === opt;
          });
          if (matches) {
            bestValue = v.options[gIdx];
            return true;
          }
          return false;
        });
      }

      if (bestValue && bestValue !== selectedOptions[gIdx]) {
        selectOptionInGroup(section, gIdx, bestValue);
      }
    });
  }

  function selectOptionInGroup(section, groupIdx, value) {
    var group = section.querySelectorAll(".joy-variant-group")[groupIdx];
    if (!group) return;

    var swatches = group.querySelectorAll(".joy-color-swatch");
    var sizeBtns = group.querySelectorAll(".joy-size-btn");
    var dropdown = group.querySelector(".joy-select");

    if (swatches.length > 0) {
      swatches.forEach(function (s) {
        s.classList.toggle("joy-color-swatch--selected", s.dataset.value === value);
      });
    } else if (sizeBtns.length > 0) {
      sizeBtns.forEach(function (b) {
        b.classList.toggle("joy-size-btn--selected", b.dataset.value === value);
      });
    } else if (dropdown) {
      dropdown.value = value;
    }

    var label = section.querySelector("#SelectedOption" + (groupIdx + 1));
    if (label) label.textContent = value;
  }

  function deselectOptionInGroup(section, groupIdx) {
    var group = section.querySelectorAll(".joy-variant-group")[groupIdx];
    if (!group) return;

    group.querySelectorAll(".joy-color-swatch").forEach(function (s) {
      s.classList.remove("joy-color-swatch--selected");
    });
    group.querySelectorAll(".joy-size-btn").forEach(function (b) {
      b.classList.remove("joy-size-btn--selected");
    });
    var dropdown = group.querySelector(".joy-select");
    if (dropdown) dropdown.value = "";

    var label = section.querySelector("#SelectedOption" + (groupIdx + 1));
    if (label) label.textContent = "—";
  }

  // ============================================
  // UPDATE VARIANT / UI STATE
  // ============================================

  function updateVariantAndUI(section, product, variantSelect) {
    var selectedOptions = getSelectedOptions(section);
    var clearBtn = section.querySelector("#ClearSelections");
    var btn = section.querySelector("#AddToCart");
    var btnText = section.querySelector("#AddToCartText");
    var priceEl = section.querySelector('[id^="Price-"]');
    var skuEl = section.querySelector("#ProductSku");

    if (clearBtn) {
      clearBtn.style.display = hasAnySelection(selectedOptions) ? "" : "none";
    }

    if (!allSelected(selectedOptions)) {
      if (btn && btnText) {
        btn.disabled = true;
        btnText.textContent = "SELECT OPTIONS";
      }

      if (priceEl) {
        var minPrice = Infinity, maxPrice = 0;
        product.variants.forEach(function (v) {
          var matchesPartial = selectedOptions.every(function (opt, i) {
            if (opt === null) return true;
            return v.options[i] === opt;
          });
          if (matchesPartial) {
            if (v.price < minPrice) minPrice = v.price;
            if (v.price > maxPrice) maxPrice = v.price;
          }
        });
        if (minPrice === maxPrice) {
          priceEl.innerHTML = '<span class="joy-product__price">' + formatMoney(minPrice) + '</span>';
        } else {
          priceEl.innerHTML = '<span class="joy-product__price">' + formatMoney(minPrice) + ' – ' + formatMoney(maxPrice) + '</span>';
        }
      }

      if (skuEl) skuEl.textContent = "—";
      return;
    }

    var variant = product.variants.find(function (v) {
      return selectedOptions.every(function (opt, i) { return v.options[i] === opt; });
    });

    if (variant) {
      variantSelect.value = variant.id;

      var url = new URL(window.location);
      url.searchParams.set("variant", variant.id);
      history.replaceState({}, "", url);

      if (priceEl) {
        var priceHtml = '<span class="joy-product__price">' + formatMoney(variant.price) + '</span>';
        if (variant.compare_at_price > variant.price) {
          priceHtml += '<span class="joy-product__price joy-product__price--compare"><del>' + formatMoney(variant.compare_at_price) + '</del></span>';
        }
        priceEl.innerHTML = priceHtml;
      }

      if (skuEl) skuEl.textContent = variant.sku || "N/A";

      if (btn && btnText) {
        if (variant.available) {
          btn.disabled = false;
          btnText.innerHTML =
            '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 4H5.5L6.5 14H16.5L18 6H7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><circle cx="8" cy="17" r="1.5" fill="currentColor"/><circle cx="15" cy="17" r="1.5" fill="currentColor"/></svg> ADD TO CART';
        } else {
          btn.disabled = true;
          btnText.textContent = "SOLD OUT";
        }
      }

      showImageForVariant(section, variant);
    } else {
      if (btn && btnText) {
        btn.disabled = true;
        btnText.textContent = "UNAVAILABLE";
      }
    }
  }

  // ============================================
  // CLEAR ALL SELECTIONS
  // ============================================

  function clearAllSelections(section, product, variantSelect) {
    var groups = section.querySelectorAll(".joy-variant-group");
    groups.forEach(function (group, gIdx) {
      deselectOptionInGroup(section, gIdx);
    });

    showAllMedia(section);
    updateOptionAvailability(section, product);
    updateVariantAndUI(section, product, variantSelect);

    var url = new URL(window.location);
    url.searchParams.delete("variant");
    history.replaceState({}, "", url);
  }

  // ============================================
  // VARIANT PICKER INIT
  // ============================================

  function initVariantPicker() {
    document.querySelectorAll(".joy-product-section").forEach(function (section) {
      var productJson = section.querySelector('[id^="ProductJson-"]');
      if (!productJson) return;

      var product = JSON.parse(productJson.textContent);
      var variantSelect = section.querySelector('[id^="VariantSelect-"]');

      var colorOptionIndex = -1;
      product.options.forEach(function (optName, i) {
        var lower = optName.toLowerCase();
        if (lower === "color" || lower === "colour") colorOptionIndex = i;
      });

      // ---- COLOR SWATCH CLICK ----
      section.querySelectorAll(".joy-color-swatch").forEach(function (swatch) {
        swatch.addEventListener("click", function () {
          var optionIndex = parseInt(this.dataset.optionIndex);
          var value = this.dataset.value;
          var isAlreadySelected = this.classList.contains("joy-color-swatch--selected");

          if (isAlreadySelected) {
            deselectOptionInGroup(section, optionIndex);
            showAllMedia(section);
          } else {
            this.closest(".joy-color-swatches")
              .querySelectorAll(".joy-color-swatch")
              .forEach(function (s) { s.classList.remove("joy-color-swatch--selected"); });
            this.classList.add("joy-color-swatch--selected");

            var label = section.querySelector("#SelectedOption" + (optionIndex + 1));
            if (label) label.textContent = value;

            showImagesForColor(section, product, optionIndex, value);
            autoCorrectOtherOptions(section, product, optionIndex);
          }

          updateOptionAvailability(section, product);
          updateVariantAndUI(section, product, variantSelect);
        });
      });

      // ---- SIZE BUTTON CLICK ----
      section.querySelectorAll(".joy-size-btn").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var optionIndex = parseInt(this.dataset.optionIndex);
          var value = this.dataset.value;
          var isAlreadySelected = this.classList.contains("joy-size-btn--selected");

          if (isAlreadySelected) {
            deselectOptionInGroup(section, optionIndex);
          } else {
            this.closest(".joy-size-buttons")
              .querySelectorAll(".joy-size-btn")
              .forEach(function (b) { b.classList.remove("joy-size-btn--selected"); });
            this.classList.add("joy-size-btn--selected");

            var label = section.querySelector("#SelectedOption" + (optionIndex + 1));
            if (label) label.textContent = value;

            autoCorrectOtherOptions(section, product, optionIndex);
          }

          updateOptionAvailability(section, product);
          updateVariantAndUI(section, product, variantSelect);
        });
      });

      // ---- DROPDOWN CHANGE ----
      section.querySelectorAll(".joy-select").forEach(function (select) {
        select.addEventListener("change", function () {
          var optionIndex = parseInt(this.dataset.optionIndex);
          if (this.value === "") {
            deselectOptionInGroup(section, optionIndex);
          } else {
            var label = section.querySelector("#SelectedOption" + (optionIndex + 1));
            if (label) label.textContent = this.value;
            autoCorrectOtherOptions(section, product, optionIndex);
          }

          updateOptionAvailability(section, product);
          updateVariantAndUI(section, product, variantSelect);
        });
      });

      // ---- CLEAR BUTTON ----
      var clearBtn = section.querySelector("#ClearSelections");
      if (clearBtn) {
        clearBtn.addEventListener("click", function () {
          clearAllSelections(section, product, variantSelect);
        });
      }

      // ---- INITIAL STATE: nothing selected ----
      showAllMedia(section);
      updateOptionAvailability(section, product);
      updateVariantAndUI(section, product, variantSelect);
    });
  }

  // ============================================
  // THUMBNAIL SLIDER
  // ============================================

  function initThumbnailSlider() {
    document.querySelectorAll(".joy-product__thumbnails-container").forEach(function (container) {
      var thumbnails = container.querySelectorAll(".joy-product__thumbnail");
      if (thumbnails.length === 0) return;

      thumbnails.forEach(function (thumb) {
        thumb.addEventListener("click", function () {
          var section = this.closest(".joy-product-section");
          var mediaId = this.dataset.mediaId;
          var images = section.querySelectorAll(".joy-product__image");

          images.forEach(function (img) {
            if (img.dataset.mediaId === mediaId) {
              animateImageIn(img);
              img.classList.add("is-active");
            } else {
              animateImageOut(img);
              img.classList.remove("is-active");
            }
          });

          container.querySelectorAll(".joy-product__thumbnail").forEach(function (t) {
            t.classList.toggle("joy-product__thumbnail--active", t.dataset.mediaId === mediaId);
          });

          animateThumbnailPop(this);
        });
      });
    });
  }

  // ============================================
  // QUANTITY
  // ============================================

  function initQuantity() {
    var minusBtn = document.getElementById("QuantityMinus");
    var plusBtn = document.getElementById("QuantityPlus");
    var input = document.getElementById("Quantity");

    if (minusBtn && input) {
      minusBtn.addEventListener("click", function () {
        var val = parseInt(input.value) || 1;
        if (val > 1) input.value = val - 1;
      });
    }
    if (plusBtn && input) {
      plusBtn.addEventListener("click", function () {
        var val = parseInt(input.value) || 1;
        input.value = val + 1;
      });
    }
  }

  // ============================================
  // ADD TO CART
  // ============================================

  function initAddToCart() {
    var form = document.getElementById("ProductForm");
    if (!form) return;

    form.addEventListener("submit", function (e) {
      e.preventDefault();

      var btn = document.getElementById("AddToCart");
      var btnText = document.getElementById("AddToCartText");
      var btnLoading = document.getElementById("AddToCartLoading");

      if (!btn || btn.disabled) return;

      btn.disabled = true;
      if (btnText) btnText.style.display = "none";
      if (btnLoading) btnLoading.style.display = "flex";

      var formData = new FormData(form);

      fetch("/cart/add.js", { method: "POST", body: formData })
        .then(function (response) {
          return response.json().then(function (data) {
            if (response.ok) {
              updateCartCount();
              if (btnText) {
                btnText.innerHTML = "✓ ADDED!";
                btnText.style.display = "flex";
              }
              if (btnLoading) btnLoading.style.display = "none";
              btn.disabled = false;
              setTimeout(function () {
                if (btnText) {
                  btnText.innerHTML =
                    '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 4H5.5L6.5 14H16.5L18 6H7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><circle cx="8" cy="17" r="1.5" fill="currentColor"/><circle cx="15" cy="17" r="1.5" fill="currentColor"/></svg> ADD TO CART';
                }
              }, 2000);
            } else {
              throw new Error(data.description || "Error adding to cart");
            }
          });
        })
        .catch(function (error) {
          alert(error.message || "Could not add to cart");
          if (btnText) btnText.style.display = "flex";
          if (btnLoading) btnLoading.style.display = "none";
          btn.disabled = false;
        });
    });
  }

  // ============================================
  // WISHLIST
  // ============================================

  function initWishlist() {
    var wishlist = getWishlist();
    document.querySelectorAll("[data-handle]").forEach(function (btn) {
      if (wishlist.indexOf(btn.dataset.handle) !== -1) btn.classList.add("is-active");
    });

    var wishlistBtn = document.getElementById("WishlistBtn");
    if (wishlistBtn) {
      wishlistBtn.addEventListener("click", function () {
        var handle = this.dataset.handle;
        var list = getWishlist();
        if (list.indexOf(handle) !== -1) {
          list = list.filter(function (h) { return h !== handle; });
          this.classList.remove("is-active");
        } else {
          list.push(handle);
          this.classList.add("is-active");
        }
        saveWishlist(list);
        var text = document.getElementById("WishlistText");
        if (text) text.textContent = list.indexOf(handle) !== -1 ? "✓ Added to Wishlist" : "+ Add to Wishlist";
      });
    }

    document.addEventListener("click", function (e) {
      var btn = e.target.closest(".joy-product-card__wishlist");
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var handle = btn.dataset.handle;
      var list = getWishlist();
      if (list.indexOf(handle) !== -1) {
        list = list.filter(function (h) { return h !== handle; });
        btn.classList.remove("is-active");
      } else {
        list.push(handle);
        btn.classList.add("is-active");
      }
      saveWishlist(list);
    });
  }

  // ============================================
  // PRODUCT TABS
  // ============================================

  function initTabs() {
    document.querySelectorAll(".joy-tabs__btn").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var container = this.closest(".joy-tabs-section");
        var tabId = this.dataset.tab;
        container.querySelectorAll(".joy-tabs__btn").forEach(function (b) {
          b.classList.toggle("joy-tabs__btn--active", b.dataset.tab === tabId);
        });
        container.querySelectorAll(".joy-tabs__panel").forEach(function (panel) {
          panel.classList.toggle("joy-tabs__panel--active", panel.dataset.panel === tabId);
        });
      });
    });
  }

  // ============================================
  // PRODUCT CARDS (Related)
  // ============================================

  function initProductCards() {
    document.querySelectorAll(".joy-product-card").forEach(function (card) {
      var images = card.querySelectorAll(".joy-product-card__image");
      var thumbnails = card.querySelectorAll(".joy-product-card__thumb");
      var prevBtn = card.querySelector(".joy-product-card__nav--prev");
      var nextBtn = card.querySelector(".joy-product-card__nav--next");
      var quickAddBtn = card.querySelector(".joy-product-card__add-to-cart");

      var currentIndex = 0;
      var totalImages = images.length;

      function showImage(index) {
        images.forEach(function (img, i) {
          img.style.display = i === index ? "block" : "none";
          img.classList.toggle("joy-product-card__image--active", i === index);
        });
        thumbnails.forEach(function (thumb, i) {
          thumb.classList.toggle("joy-product-card__thumb--active", i === index);
        });
        currentIndex = index;
      }

      thumbnails.forEach(function (thumb, i) {
        thumb.addEventListener("click", function (e) {
          e.preventDefault(); e.stopPropagation(); showImage(i);
        });
      });

      if (prevBtn) {
        prevBtn.addEventListener("click", function (e) {
          e.preventDefault(); e.stopPropagation();
          showImage(currentIndex === 0 ? totalImages - 1 : currentIndex - 1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener("click", function (e) {
          e.preventDefault(); e.stopPropagation();
          showImage(currentIndex === totalImages - 1 ? 0 : currentIndex + 1);
        });
      }

      if (quickAddBtn) {
        quickAddBtn.addEventListener("click", function (e) {
          e.preventDefault(); e.stopPropagation();
          var variantId = this.dataset.variantId;
          var self = this;
          self.style.opacity = "0.6"; self.disabled = true;
          fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
          })
          .then(function (r) {
            if (r.ok) {
              self.style.opacity = "1"; self.style.transform = "scale(1.2)";
              updateCartCount();
              setTimeout(function () {
                self.style.opacity = ""; self.style.transform = ""; self.disabled = false;
              }, 1000);
            } else { throw new Error("Failed"); }
          })
          .catch(function () {
            self.style.opacity = ""; self.style.transform = ""; self.disabled = false;
            alert("Could not add to cart");
          });
        });
      }
    });
  }

  // ============================================
  // COLOR SWATCH RESOLVER — dynamic CSS color detection + fallback map
  // Uses the browser itself to test if a color name is valid CSS,
  // so ANY valid CSS color keyword works without hardcoding.
  // Extended map only for non-standard names (rainbow, multicolor, etc.)
  // Split logic for two-tone colors ("Black & White" → half/half).
  // ============================================

  var _colorTestEl = null;

  function isCSSColor(name) {
    if (!_colorTestEl) {
      _colorTestEl = document.createElement("div");
      _colorTestEl.style.display = "none";
      document.body.appendChild(_colorTestEl);
    }
    _colorTestEl.style.color = "";
    _colorTestEl.style.color = name;
    return _colorTestEl.style.color !== "";
  }

  function getCSSColorValue(name) {
    if (!_colorTestEl) {
      _colorTestEl = document.createElement("div");
      _colorTestEl.style.display = "none";
      document.body.appendChild(_colorTestEl);
    }
    _colorTestEl.style.color = "";
    _colorTestEl.style.color = name;
    if (_colorTestEl.style.color === "") return null;
    var computed = window.getComputedStyle(_colorTestEl).color;
    return computed || name;
  }

  var EXTENDED_COLOR_MAP = {
    rainbow: "linear-gradient(135deg, #FF0000 0%, #FF8C00 17%, #FFD700 33%, #228B22 50%, #4169E1 67%, #8B008B 83%, #FF69B4 100%)",
    multicolor: "linear-gradient(135deg, #FF0000 0%, #FF8C00 25%, #228B22 50%, #4169E1 75%, #8B008B 100%)",
    "multi-color": "linear-gradient(135deg, #FF0000 0%, #FF8C00 25%, #228B22 50%, #4169E1 75%, #8B008B 100%)",
    nude: "#E8C4A0", champagne: "#F7E7CE", taupe: "#483C32",
    mocha: "#967969", caramel: "#FFD59A", coffee: "#6F4E37",
    rust: "#B7410E", copper: "#B87333", mustard: "#FFDB58",
    sage: "#BCB88A", mint: "#98FF98", jade: "#00A86B",
    hunter: "#355E3B", moss: "#8A9A5B", forest: "#228B22",
    cobalt: "#0047AB", denim: "#1560BD", cerulean: "#007BA7",
    mauve: "#E0B0FF", lilac: "#C8A2C8", amethyst: "#9966CC",
    eggplant: "#614051", burgundy: "#800020", wine: "#722F37",
    blush: "#DE5D83", rose: "#FF007F", peach: "#FFCBA4",
    cream: "#FFFDD0", ivory: "#FFFFF0", pearl: "#F0EAD6",
    charcoal: "#36454F", graphite: "#383838", ash: "#B2BEB5",
    walnut: "#773F1A", chestnut: "#954535", espresso: "#3C1414",
    oatmeal: "#D4C5A9", sand: "#C2B280", stone: "#928E85",
    dusty: "#B5978E", pastel: "#CBAACB", neon: "#39FF14",
    floral: "#FF69B4", tropical: "#FF8C00", paisley: "#8B008B",
    daisy: "#4169E1", sunset: "#FAD6A5", ocean: "#006994",
    sky: "#87CEEB", midnight: "#191970", berry: "#8E4585"
  };

  var SPLIT_SEPARATORS = [" & ", " and ", " / ", "/"];

  function resolveOneColor(name) {
    name = name.trim().toLowerCase();

    if (isCSSColor(name)) return getCSSColorValue(name);

    if (EXTENDED_COLOR_MAP[name]) return EXTENDED_COLOR_MAP[name];

    var words = name.split(/[\s\-_]+/);
    for (var i = 0; i < words.length; i++) {
      if (isCSSColor(words[i])) return getCSSColorValue(words[i]);
      if (EXTENDED_COLOR_MAP[words[i]]) return EXTENDED_COLOR_MAP[words[i]];
    }

    for (var key in EXTENDED_COLOR_MAP) {
      if (name.indexOf(key) !== -1) return EXTENDED_COLOR_MAP[key];
    }

    return null;
  }

  function resolveColorStyle(colorName) {
    var name = (colorName || "").toLowerCase().trim();

    for (var s = 0; s < SPLIT_SEPARATORS.length; s++) {
      var sep = SPLIT_SEPARATORS[s];
      var idx = name.indexOf(sep.toLowerCase());
      if (idx !== -1) {
        var part1 = name.substring(0, idx).trim();
        var part2 = name.substring(idx + sep.length).trim();
        var c1 = resolveOneColor(part1);
        var c2 = resolveOneColor(part2);
        if (c1 && c2) {
          return "linear-gradient(135deg, " + c1 + " 50%, " + c2 + " 50%)";
        }
      }
    }

    var resolved = resolveOneColor(name);
    if (resolved) return resolved;

    return null;
  }

  function isLightColor(cssColor) {
    if (!cssColor) return false;
    var m = cssColor.match(/rgb[a]?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (!m) {
      var lower = cssColor.toLowerCase();
      return lower === "white" || lower === "#ffffff" || lower === "#fff" ||
             lower === "ivory" || lower === "snow" || lower === "cream" ||
             lower === "rgb(255, 255, 255)";
    }
    var lum = (parseInt(m[1]) * 299 + parseInt(m[2]) * 587 + parseInt(m[3]) * 114) / 1000;
    return lum > 230;
  }

  function initColorSwatches() {
    document.querySelectorAll(".joy-color-fallback[data-color-name]").forEach(function (el) {
      var colorName = el.getAttribute("data-color-name");
      var style = resolveColorStyle(colorName);
      if (style) {
        if (style.indexOf("gradient") !== -1) {
          el.style.background = style;
        } else {
          el.style.backgroundColor = style;
        }

        if (isLightColor(style)) {
          el.closest(".joy-color-swatch").classList.add("joy-color-swatch--solid-white");
        }
      } else {
        el.style.backgroundColor = "#ccc";
        el.style.border = "1px dashed #999";
      }
    });
  }

  // ============================================
  // GSAP ENTRANCE & HOVER (transform-only, safe)
  // ============================================

  function initGsapEntranceAnimations() {
    if (!gsapReady || typeof ScrollTrigger === "undefined") return;
    gsap.registerPlugin(ScrollTrigger);

    gsap.utils.toArray(".joy-product-section").forEach(function (section) {
      var media = section.querySelector(".joy-product__media");
      var info = section.querySelector(".joy-product__info");

      if (media) {
        gsap.set(media, { x: -40 });
        ScrollTrigger.create({
          trigger: media, start: "top 85%", once: true,
          onEnter: function () { gsap.to(media, { x: 0, duration: 0.7, ease: "power3.out" }); }
        });
      }
      if (info) {
        gsap.set(info, { x: 40 });
        ScrollTrigger.create({
          trigger: info, start: "top 85%", once: true,
          onEnter: function () { gsap.to(info, { x: 0, duration: 0.7, ease: "power3.out", delay: 0.15 }); }
        });
      }
    });

    gsap.utils.toArray(".joy-product-card").forEach(function (card, i) {
      gsap.set(card, { y: 30 });
      ScrollTrigger.create({
        trigger: card, start: "top 90%", once: true,
        onEnter: function () { gsap.to(card, { y: 0, duration: 0.5, delay: i * 0.1, ease: "power2.out" }); }
      });
    });
  }

  function initGsapHoverEffects() {
    if (!gsapReady) return;

    document.querySelectorAll(".joy-color-swatch").forEach(function (swatch) {
      swatch.addEventListener("mouseenter", function () {
        gsap.to(this, { scale: 1.15, duration: 0.2, ease: "power2.out" });
      });
      swatch.addEventListener("mouseleave", function () {
        gsap.to(this, { scale: 1, duration: 0.2, ease: "power2.out" });
      });
    });

    document.querySelectorAll(".joy-size-btn").forEach(function (btn) {
      btn.addEventListener("mouseenter", function () {
        gsap.to(this, { scale: 1.08, duration: 0.15, ease: "power2.out" });
      });
      btn.addEventListener("mouseleave", function () {
        gsap.to(this, { scale: 1, duration: 0.15, ease: "power2.out" });
      });
    });

    document.querySelectorAll(".joy-product__thumbnail").forEach(function (thumb) {
      thumb.addEventListener("mouseenter", function () {
        gsap.to(this, { scale: 1.08, duration: 0.2, ease: "power2.out" });
      });
      thumb.addEventListener("mouseleave", function () {
        gsap.to(this, { scale: 1, duration: 0.2, ease: "power2.out" });
      });
    });

    document.querySelectorAll(".joy-add-to-cart-btn").forEach(function (btn) {
      btn.addEventListener("mouseenter", function () {
        if (!this.disabled) gsap.to(this, { scale: 1.03, duration: 0.2, ease: "power2.out" });
      });
      btn.addEventListener("mouseleave", function () {
        gsap.to(this, { scale: 1, duration: 0.2, ease: "power2.out" });
      });
    });

    document.querySelectorAll(".joy-product-card").forEach(function (card) {
      card.addEventListener("mouseenter", function () {
        gsap.to(this, { y: -4, duration: 0.3, ease: "power2.out" });
      });
      card.addEventListener("mouseleave", function () {
        gsap.to(this, { y: 0, duration: 0.3, ease: "power2.out" });
      });
    });
  }

  // ============================================
  // INIT
  // ============================================

  function init() {
    gsapReady = typeof gsap !== "undefined";
    initColorSwatches();
    initThumbnailSlider();
    initVariantPicker();
    initQuantity();
    initAddToCart();
    initWishlist();
    initTabs();
    initProductCards();
    initGsapEntranceAnimations();
    initGsapHoverEffects();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
